import React, { useRef, useEffect, useState } from 'react';
import ChatHeader from './ChatHeader';
import MessagesList from './MessagesList';
import MessageInput from './MessageInput';
import useGetChatMessages from '../../hooks/chats/useGetChatMessages';
import { useSocket } from '../../context/SocketContext';
import Cookies from 'universal-cookie';
import { COMPANY_ID, COMPANY_EMPLOYEE_ID, PRODUCT_SLUG } from '../../config';
import toast from 'react-hot-toast';
import PrimaryLoader from '../../components/common/PrimaryLoader';

const cookies = new Cookies();

const ChatWindow = ({ 
  selectedChat, 
  message, 
  setMessage, 
  handleMessageChange, 
  closeChat,
  refreshNeedsAttention,
  refreshChatList
}) => {
  const { isConnected, emit, on, removeListener, registerChatRoom, unregisterChatRoom } = useSocket();
  
  const messagesContainerRef = useRef(null);
  const hasScrolledInitial = useRef(false); 
  const [isNewMessageSent, setIsNewMessageSent] = useState(false);
  const [socketChatId, setSocketChatId] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const prevScrollHeightRef = useRef(null);
  
  // Message queue and processing state
  const messageQueueRef = useRef([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  
  // Get user IDs from cookies
  const company_id = cookies.get(COMPANY_ID);
  const self_employee_id = cookies.get(COMPANY_EMPLOYEE_ID);
  
  // State to manage local messages
  const [localMessages, setLocalMessages] = useState([]);
  const [hasLoadedMessages, setHasLoadedMessages] = useState(false);
  
  // Hook to get chat messages
  const { 
    messages, 
    loading, 
    loadMoreMessages,
    isFirstLoad,
    hasMoreMessages,
    refetch,
    isFetching
  } = useGetChatMessages(socketChatId, 10, 1);
  
  // Sync API messages with local state
  useEffect(() => {
    if (messages && messages.length > 0) {
      setLocalMessages(prevMessages => {
        // Keep optimistic messages separately.
        const optimisticMessages = prevMessages.filter(m => m.isOptimistic);
        // Preserve non-optimistic messages already in local state not present in the fetched messages.
        const nonOptimisticLocal = prevMessages.filter(m => !m.isOptimistic);
        const missingMessages = nonOptimisticLocal.filter(
          m => !messages.some(fetched => fetched.message_id === m.message_id)
        );
        return [...messages, ...missingMessages, ...optimisticMessages];
      });
      
      // Check for active needs attention messages from REST API
      const activeNeedsAttentionMessages = messages.filter(message => 
        message.msg_type === 'needs_attention' && 
        message.attention_completion_status === null &&
        !message.selfSender
      );
      
      // console.log("Active needs attention messages found:", activeNeedsAttentionMessages.length);
      
      // Update the selectedChat status if needed
      if (activeNeedsAttentionMessages.length > 0 && selectedChat?.chat) {
        // console.log("Setting is_needs_attention to 1");
        selectedChat.chat.is_needs_attention = 1;
      }
      
      setHasLoadedMessages(true);
    }
  }, [messages, selectedChat]);
  
  // Track chat rooms that have been left to prevent duplicate leave-room events
  const leftChatRooms = useRef(new Set());
  
  // Initialize socket connection when component mounts or when selectedChat changes
  useEffect(() => {
    if (!selectedChat) return;
    
    // Reset states for new chat - but preserve localMessages if we already have some
    if (!localMessages.length || selectedChat.other_member_id !== prevOtherMemberId.current) {
      // If we have an active previous chat, emit leave-room for that one first
      if (prevOtherMemberId.current && socketChatId && isConnected) {
        // Only emit leave-room if we haven't already left this room
        if (!leftChatRooms.current.has(socketChatId)) {
          // console.log(`Leaving previous chat room with ID: ${socketChatId}`);
          emit("leave-room", { chat_id: socketChatId });
          leftChatRooms.current.add(socketChatId);
          // Unregister the previous chat room
          unregisterChatRoom(socketChatId);
        }
      }
      
      setLocalMessages([]);
      setHasLoadedMessages(false);
      hasScrolledInitial.current = false;
    }
    
    // Store current other_member_id for comparison on next change
    prevOtherMemberId.current = selectedChat.other_member_id;
    
    // Store current chat_id for potential leave-room
    if (selectedChat.chat_id) {
      setSocketChatId(selectedChat.chat_id);
      // Reset the tracking of this chat room as we're joining it
      leftChatRooms.current.delete(selectedChat.chat_id);
    }
    
    // Don't initialize socket here - use the global one instead
    joinChatRoom();
    
    // Clean up socket listeners when component unmounts or selectedChat changes
    return () => {
      // If we have an active socketChatId, emit leave-room before cleanup
      if (socketChatId && isConnected) {
        // Only emit leave-room if we haven't already left this room
        if (!leftChatRooms.current.has(socketChatId)) {
          // console.log(`Leaving chat room with ID: ${socketChatId} on cleanup`);
          emit("leave-room", { chat_id: socketChatId });
          leftChatRooms.current.add(socketChatId);
          // Unregister the chat room when leaving
          unregisterChatRoom(socketChatId);
        }
      }
      
      removeListener("receive-message");
      removeListener("needs_attention_completed");
    };
  }, [selectedChat?.other_member_id, isConnected]);
  
  const joinChatRoom = () => {
    if (!isConnected) {
      // console.log("Socket not connected yet, waiting...");
      return;
    }
    
    // console.log("Joining chat room with:", selectedChat?.other_member?.first_name);
    
    // Payload for joining the room
    const joinRoomData = {
      self_employee_id: Number(self_employee_id),
      other_employee_id: Number(selectedChat.other_member_id),
      company_id: Number(company_id),
      product_slug: PRODUCT_SLUG
    };
    
    // Log the data
    // console.log("EMITTING join-room with data:", joinRoomData);
    
    emit(
      "join-room",
      joinRoomData,
      (response) => {
        // console.log("Join room response:", response);
        if (response) {
          if (response.chatData && response.chatData.chatId) {
            const newChatId = Number(response.chatData.chatId);
            setSocketChatId(newChatId);
            // console.log(`Chat ID set to: ${newChatId}`);
            // Register the new chat room with the context
            registerChatRoom(newChatId);
          } else {
            // Fall back to using the chat_id from selectedChat if available
            if (selectedChat.chat_id) {
              setSocketChatId(selectedChat.chat_id);
              // console.log(`Using chat ID from selectedChat: ${selectedChat.chat_id}`);
              // Register the existing chat room with the context
              registerChatRoom(selectedChat.chat_id);
            } else {
              // console.error("Could not determine chat_id from response or selectedChat");
            }
          }
        } 
      }
    );
    

    on("receive-message", (response) => {
      // console.log("Received new message:", response);
      
      if (response && response.message_id) {
        // Process the message correctly based on the server's response format
        const enhancedMessage = {
          ...response,
          // Always ensure consistent selfSender property (the server returns self_sender)
          selfSender: response.self_sender,
          // Map is_needs_attention to msg_type for UI consistency
          msg_type: response.is_needs_attention === 1 ? 'needs_attention' : 'message',
          // Ensure attention_completion_status is present for needs attention messages
          ...(response.is_needs_attention === 1 && {
            attention_completion_status: response.attention_completion_status || null
          })
        };
        
        // console.log("Adding message to current chat:", enhancedMessage);
        setLocalMessages(prevMessages => [...prevMessages, enhancedMessage]);
        
        // Update selectedChat.chat.is_needs_attention when receiving a needs attention message
        if (response.is_needs_attention === 1 && !response.self_sender && selectedChat?.chat) {
          selectedChat.chat.is_needs_attention = 1;
          
          // Refresh the needs attention list when receiving a needs attention message
          refreshNeedsAttention();
        }
        
        // Trigger scroll to bottom
        setIsNewMessageSent(true);
      }
    });
    
    // Similar fix for needs_attention_completed handler
    removeListener("needs_attention_completed");
    on("needs_attention_completed", (response) => {
      // console.log("Needs attention status changed:", response);
      
      if (response && response.chat_message_id) {
        // Remove the chat_id check here too
        setLocalMessages(prev => 
          prev.map(msg => 
            msg.message_id === response.chat_message_id 
              ? { ...msg, attention_completion_status: response.status } 
              : msg
          )
        );
        
        // Update the selected chat to reflect that needs attention is now 0
        if (selectedChat.chat) {
          selectedChat.chat.is_needs_attention = 0;
        }
        
        // Show toast notification if the message was not sent by the current user
        if (response.completed_by !== Number(self_employee_id)) {
          const actionText = response.status === 'resolve' 
            ? 'resolved' 
            : 'revoked';
          
          toast.success(`Attention request ${actionText} by ${selectedChat.other_member.first_name}`);
        }
        
        // Refresh both needs attention and chat list
        refreshNeedsAttention();
        refreshChatList(); // Add this line to refresh the main chat list
      }
    });
  };
  
  // Function to send a message using socket
  const sendSocketMessage = (msgObj, queueCallback) => {
    if (!socketChatId) {
      queueCallback && queueCallback();
      return;
    }
    setIsSending(true);
    
    const { text, msgType, tempId } = msgObj;
    
    const messageData = {
      self_employee_id: Number(self_employee_id),
      other_employee_id: Number(selectedChat.other_member_id),
      company_id: Number(company_id),
      product_slug: PRODUCT_SLUG,
      chat_id: socketChatId,
      message: msgType === "needs_attention" ? "I need your attention!" : text,
      msg_type: msgType
    };
    
    emit(
      "send-message",
      messageData,
      (response) => {
        // console.log("Message send response:", response);
        setIsSending(false);
        
        if (response?.success) {
          if (response.data) {
            const selfSender = response.data.selfSender ?? response.data.self_sender ?? true;
            const enhancedMessageData = {
              ...response.data,
              selfSender,
              ...(response.data.self_sender && { self_sender: undefined }),
              msg_type: response.data.is_needs_attention === 1 ? 'needs_attention' : 'message',
              ...(response.data.is_needs_attention === 1 && {
                attention_completion_status: response.data.attention_completion_status || null
              })
            };
            
            // Replace the optimistic message in its original array position
            setLocalMessages(prev =>
              prev.map(msg =>
                msg.message_id === tempId ? enhancedMessageData : msg
              )
            );
            
            setHasLoadedMessages(true);
            if (msgType === "needs_attention" && selectedChat.chat) {
              selectedChat.chat.is_needs_attention = 1;
              
              // Refresh needs attention list specifically
              refreshNeedsAttention();
            }
          }
          setIsNewMessageSent(true);
          
          // Refresh the chat list after sending a message
          refreshChatList();
        } else {
          setLocalMessages(prev =>
            prev.map(msg =>
              msg.message_id === tempId
                ? { ...msg, failed: true, isOptimistic: false }
                : msg
            )
          );
          toast.error("Failed to send message");
        }
        queueCallback && queueCallback();
      }
    );
  };
  
  // Wrapped send message function: push message into queue and trigger processing.
  const handleSendWrapped = () => {
    if (message.trim() === '') return;
    const tempId = `temp-${Date.now()}`;
    // Immediately add the optimistic message so it appears in the UI
    const optimisticMessage = {
      message_id: tempId,
      first_name: "You", 
      last_name: "",
      message: message,
      msg_type: "message",
      attention_completion_status: null,
      created_on: new Date().toISOString(),
      selfSender: true,
      is_needs_attention: 0,
      isOptimistic: true
    };
    setLocalMessages(prev => [...prev, optimisticMessage]);
    setIsNewMessageSent(true); 
    
    // Push the message object (with tempId) into the queue
    messageQueueRef.current.push({ text: message, msgType: "message", tempId });
    setMessage("");
    if (!isProcessingQueue) {
      processMessageQueue();
    }
  };
  
  // Wrapped needs attention function: queue the needs_attention message.
  const handleNeedsAttentionWrapped = () => {
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      message_id: tempId,
      first_name: "You",
      last_name: "",
      message: "I need your attention!",
      msg_type: "needs_attention",
      attention_completion_status: null,
      created_on: new Date().toISOString(),
      selfSender: true,
      is_needs_attention: 1,
      isOptimistic: true
    };
    setLocalMessages(prev => [...prev, optimisticMessage]);
    setIsNewMessageSent(true); // New: Scroll down after adding optimistic message
    
    messageQueueRef.current.push({ text: "", msgType: "needs_attention", tempId });
    if (!isProcessingQueue) {
      processMessageQueue();
    }
  };
  
  // Function to process the message queue sequentially.
  const processMessageQueue = () => {
    if (messageQueueRef.current.length === 0) {
      setIsProcessingQueue(false);
      return;
    }
    setIsProcessingQueue(true);
    const nextMessage = messageQueueRef.current[0];
    sendSocketMessage(nextMessage, () => {
      // Remove the processed message and process the next one.
      messageQueueRef.current.shift();
      processMessageQueue();
    });
  };
  
  // New handlers for resolving and revoking attention requests
  const handleResolveAttention = (messageId) => {
    if (!messageId) return;
    
    // console.log("Resolving attention request", messageId);
    
    emit(
      "complete-attention",
      {
        completion_status: "resolve",
        chat_message_id: Number(messageId),
        completed_by: Number(self_employee_id)
      },
      (response) => {
        // console.log("Resolve attention response:", response);
        
        if (response?.success) {
          // Update the message in the local state
          setLocalMessages(prev => 
            prev.map(msg => 
              msg.message_id === messageId 
                ? { ...msg, attention_completion_status: 'resolve' } 
                : msg
            )
          );
          
          // Update the selected chat to reflect that needs attention is now 0
          if (selectedChat.chat) {
            selectedChat.chat.is_needs_attention = 0;
          }
          
          toast.success("Attention request resolved");
          
          // Refresh both needs attention and chat list
          refreshNeedsAttention();
          refreshChatList();
        } else {
          toast.error("Failed to resolve attention request");
        }
      }
    );
  };
  
  const handleRevokeAttention = (messageId) => {
    if (!messageId) return;
    
    // console.log("Revoking attention request", messageId);
    
    emit(
      "complete-attention",
      {
        completion_status: "revoke",
        chat_message_id: Number(messageId),
        completed_by: Number(self_employee_id)
      },
      (response) => {
        // console.log("Revoke attention response:", response);
        
        if (response?.success) {
          // Update the message in the local state
          setLocalMessages(prev => 
            prev.map(msg => 
              msg.message_id === messageId 
                ? { ...msg, attention_completion_status: 'revoke' } 
                : msg
            )
          );
          
          // Update the selected chat to reflect that needs attention is now 0
          if (selectedChat.chat) {
            selectedChat.chat.is_needs_attention = 0;
          }
          
          toast.success("Attention request revoked");
          
          // Refresh both needs attention and chat list
          refreshNeedsAttention();
          refreshChatList();
        } else {
          toast.error("Failed to revoke attention request");
        }
      }
    );
  };

  // Preserve user's scroll position when older messages are loaded (infinite scroll)
  useEffect(() => {
    if (prevScrollHeightRef.current !== null && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const diff = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop = container.scrollTop + diff;
      prevScrollHeightRef.current = null;
    }
  }, [localMessages]); // localMessages updated from the hook
  
  // Auto-scroll to bottom when a new message is sent
  useEffect(() => {
    if (isNewMessageSent && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      setIsNewMessageSent(false);
    }
  }, [isNewMessageSent]);
  
  // New effect: on initial load, scroll to bottom exactly once
  useEffect(() => {
    if (hasLoadedMessages && messagesContainerRef.current && !hasScrolledInitial.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      hasScrolledInitial.current = true;
    }
  }, [hasLoadedMessages, localMessages]);

  // New wrapper for infinite scroll – record scroll height before loading more messages
  const handleLoadMoreMessages = () => {
    if (messagesContainerRef.current) {
      prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
    }
    loadMoreMessages();
  };

  // Add escape key listener to close chat
  useEffect(() => {
    const handleEscPress = (e) => {
      if (e.key === 'Escape') {
        closeChat();
      }
    };
    
    document.addEventListener('keydown', handleEscPress);
    return () => {
      document.removeEventListener('keydown', handleEscPress);
    };
  }, [closeChat]);
  
  // Add ref to track previous other_member_id to detect actual chat changes
  const prevOtherMemberId = useRef(null);

  // This effect cleans up the leftChatRooms ref when the component unmounts completely
  useEffect(() => {
    return () => {
      leftChatRooms.current.clear();
    };
  }, []);

  // Check if the chat is empty
  const isEmptyChat = (!localMessages || localMessages.length === 0) && 
                      !loading && 
                      !isFetching && 
                      !isSending;

  // Add a new socketConnecting state to show during initialization
  const socketConnecting = selectedChat && !isConnected;

  return (
    <div className="flex-1 flex flex-col">
      <ChatHeader 
        selectedChat={selectedChat} 
        closeChat={closeChat}
        handleNeedsAttention={handleNeedsAttentionWrapped}
        isSocketConnected={isConnected}
      />
      
      {socketConnecting ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-100">
          <div className="flex flex-col items-center space-y-4">
            <PrimaryLoader />
            <p className="text-gray-500">Connecting to chat...</p>
          </div>
        </div>
      ) : isEmptyChat ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-100">
          <p className="text-gray-500 mb-2">Start chatting with {selectedChat.other_member.first_name}</p>
          <p className="text-gray-400 text-sm">Your messages will appear here</p>
        </div>
      ) : (
        <MessagesList 
          messages={hasLoadedMessages ? localMessages : messages} 
          messagesContainerRef={messagesContainerRef}
          isLoading={loading && !hasLoadedMessages}
          isFetching={isFetching && !hasLoadedMessages} 
          loadMoreMessages={handleLoadMoreMessages}
          isFirstLoad={isFirstLoad}
          hasMoreMessages={hasMoreMessages}
          onResolveAttention={handleResolveAttention}
          onRevokeAttention={handleRevokeAttention}
        />
      )}
      
      <MessageInput 
        message={message}
        handleMessageChange={handleMessageChange}
        handleSendMessage={handleSendWrapped}
        handleKeyPress={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendWrapped();
          }
        }}
        disabled={!isConnected}
        isSending={isSending}
      />
    </div>
  );
};

export default ChatWindow;
